import { HttpParams } from '@angular/common/http';

/**
 * Type for values that can be converted to HTTP parameters
 */
type HttpParamValue =
	| string
	| number
	| boolean
	| Date
	| string[]
	| number[]
	| null
	| undefined;

/**
 * Type for the cleaned parameters object (only string values)
 */
type CleanedParams = Record<string, string | string[]>;

/**
 * Cleans an object by removing null and undefined values and converting remaining values to strings.
 * Arrays are converted to comma-separated strings.
 * This is useful for preparing parameters for HTTP requests.
 *
 * @param params - The parameters object to clean
 * @returns A new object with only defined values converted to strings
 *
 * @example
 * ```ts
 * const params = {
 *   id: 123,
 *   name: 'John',
 *   age: null,
 *   active: true,
 *   date: new Date('2023-01-01'),
 *   tags: ['tag1', 'tag2'],
 *   userIds: [1, 2, 3],
 *   description: undefined
 * };
 *
 * const cleaned = cleanHttpParams(params);
 * // Result: {
 * //   id: '123',
 * //   name: 'John',
 * //   active: 'true',
 * //   date: '2023-01-01T00:00:00.000Z',
 * //   tags: 'tag1,tag2',
 * //   userIds: '1,2,3'
 * // }
 * ```
 */
export function cleanHttpParams(params: any): CleanedParams {
	return Object.entries(params).reduce((acc, [key, value]) => {
		if (value !== undefined && value !== null) {
			if (value instanceof Date) {
				acc[key] = value.toISOString();
			} else if (Array.isArray(value)) {
				if (value.length > 0) {
					acc[key] = value.map(String);
				}
			} else {
				acc[key] = String(value);
			}
		}
		return acc;
	}, {} as CleanedParams);
}

/**
 * Creates HttpParams from a parameters object, automatically cleaning null/undefined values.
 * Arrays are converted to comma-separated strings.
 * This is a convenience function that combines cleanHttpParams with HttpParams creation.
 *
 * @param params - The parameters object to convert
 * @returns HttpParams instance with cleaned parameters, or undefined if no valid params
 *
 * @example
 * ```ts
 * const params = {
 *   page: 1,
 *   limit: 10,
 *   search: null,
 *   filter: undefined,
 *   categories: ['tech', 'news'],
 *   userIds: [1, 2, 3]
 * };
 *
 * const httpParams = createHttpParams(params);
 * // Result: HttpParams with { page: '1', limit: '10', categories: 'tech,news', userIds: '1,2,3' }
 * ```
 */
export function createHttpParams(params?: any): HttpParams | undefined {
	if (!params) return undefined;

	const cleanedParams = cleanHttpParams(params);
	const hasValidParams = Object.keys(cleanedParams).length > 0;

	return hasValidParams
		? new HttpParams({ fromObject: cleanedParams })
		: undefined;
}

/**
 * Type guard to check if a value is a valid HTTP parameter value (not null or undefined)
 */
export function isValidHttpParamValue(
	value: HttpParamValue
): value is Exclude<HttpParamValue, null | undefined> {
	return value !== null && value !== undefined;
}

/**
 * Filters an object to only include keys with valid HTTP parameter values
 *
 * @param params - The parameters object to filter
 * @returns A new object with only valid parameter values
 */
export function filterValidHttpParams<T extends Record<string, HttpParamValue>>(
	params: T
): Partial<T> {
	return Object.fromEntries(
		Object.entries(params).filter(([, value]) => isValidHttpParamValue(value))
	) as Partial<T>;
}

/**
 * Serializes an array to a comma-separated string for HTTP parameters
 *
 * @param array - The array to serialize
 * @returns Comma-separated string or undefined if array is empty/null/undefined
 *
 * @example
 * ```ts
 * serializeArray([1, 2, 3]); // '1,2,3'
 * serializeArray(['a', 'b']); // 'a,b'
 * serializeArray([]); // undefined
 * serializeArray(null); // undefined
 * ```
 */
export function serializeArray(
	array: any[] | null | undefined
): string | undefined {
	if (!array || !Array.isArray(array) || array.length === 0) {
		return undefined;
	}
	return array.join(',');
}

/**
 * Parses a comma-separated string back to an array of numbers
 *
 * @param value - The comma-separated string
 * @returns Array of numbers or undefined if string is empty/null/undefined
 *
 * @example
 * ```ts
 * parseNumberArray('1,2,3'); // [1, 2, 3]
 * parseNumberArray(''); // undefined
 * parseNumberArray(null); // undefined
 * ```
 */
export function parseNumberArray(
	value: string | null | undefined
): number[] | undefined {
	if (!value || typeof value !== 'string') {
		return undefined;
	}
	const trimmed = value.trim();
	if (!trimmed) {
		return undefined;
	}
	return trimmed
		.split(',')
		.map(id => Number(id.trim()))
		.filter(id => !isNaN(id));
}

/**
 * Parses a comma-separated string back to an array of strings
 *
 * @param value - The comma-separated string
 * @returns Array of strings or undefined if string is empty/null/undefined
 *
 * @example
 * ```ts
 * parseStringArray('a,b,c'); // ['a', 'b', 'c']
 * parseStringArray(''); // undefined
 * parseStringArray(null); // undefined
 * ```
 */
export function parseStringArray(
	value: string | null | undefined
): string[] | undefined {
	if (!value || typeof value !== 'string') {
		return undefined;
	}
	const trimmed = value.trim();
	if (!trimmed) {
		return undefined;
	}
	return trimmed
		.split(',')
		.map(item => item.trim())
		.filter(item => item.length > 0);
}
